import React, { useEffect, useRef, useSyncExternalStore, type PropsWithChildren } from 'react';
import ConfigProvider from 'antd/es/config-provider';
import antdTheme from 'antd/es/theme';
import type { ConfigProviderProps } from 'antd/es/config-provider';
import { palettes, type ColorRole, type ThemeId, resolveTheme, THEME_STORAGE_KEY, isDarkTheme, themeOnAccent } from './palettes';
import { externalThemeStyles } from './external-theme-presets';
import legacyColors from './legacy-colors.json';
import { useDesktopReducedMotion } from '../../shared/hooks/use-desktop-reduced-motion';
import './theme.css';
import { installDeferredScrollbarVisibility } from '../scrollbar-visibility-loader';

let current: ThemeId = 'minimal-gold';
const listeners = new Set<() => void>();
const subscribe = (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; };
const snapshot = () => current;
// Ant tokens also affect print. Restore the pre-existing geometry/font tokens
// while printing; switching this media preference must not remount form state.
const subscribePrint = (listener: () => void) => {
  const media = window.matchMedia('print');
  media.addEventListener('change', listener);
  return () => media.removeEventListener('change', listener);
};
const printSnapshot = () => window.matchMedia('print').matches;
const aliases: Record<string, ColorRole> = {
  brand: 'primary', 'brand-strong': 'primaryStrong', gold: 'primary', 'gold-strong': 'primaryStrong',
  'gold-highlight': 'accent', 'gold-soft': 'primarySoft', graphite: 'ink', ink: 'ink', text: 'text',
  muted: 'muted', subtle: 'subtle', ivory: 'canvas', canvas: 'canvas', surface: 'surface',
  'surface-soft': 'soft', line: 'border', 'line-strong': 'border', 'nav-ivory': 'nav', 'nav-hover': 'primarySoft',
  'text-strong': 'ink', 'text-primary': 'text', 'text-secondary': 'muted', 'text-tertiary': 'subtle',
  'text-disabled': 'subtle', 'icon-primary': 'muted', 'icon-warm': 'primary',
  'nav-state-default': 'nav', 'nav-state-interactive': 'accent', 'nav-state-border': 'accent',
  'nav-state-ink': 'ink', 'nav-state-accent': 'accent',
  'mobile-ivory': 'canvas', 'mobile-ivory-strong': 'soft', 'mobile-card': 'surface',
  'mobile-gold': 'primary', 'mobile-gold-strong': 'primaryStrong', 'mobile-gold-soft': 'primarySoft',
  'mobile-gold-pale': 'primarySoft', 'mobile-ink': 'ink', 'mobile-text': 'text', 'mobile-text-muted': 'muted', 'mobile-border': 'border',
  success: 'success', warning: 'warning', danger: 'danger',
  'color-primary': 'primary', 'color-primary-strong': 'primaryStrong', 'color-primary-soft': 'primarySoft',
  'color-ink': 'ink', 'color-text': 'text', 'color-muted': 'muted', 'color-subtle': 'subtle',
  'color-canvas': 'canvas', 'color-surface': 'surface', 'color-surface-subtle': 'soft',
  'color-success-text': 'success', 'color-success-soft': 'successSoft',
  'color-warning-text': 'warning', 'color-warning-soft': 'warningSoft',
  'color-danger-text': 'danger', 'color-danger-soft': 'dangerSoft',
};

function applyTheme(id: ThemeId) {
  current = id;
  const root = document.documentElement;
  root.dataset.theme = id;
  root.dataset.themeStyle = id in externalThemeStyles ? 'complete' : 'classic';
  root.style.colorScheme = isDarkTheme(id) ? 'dark' : 'light';
  root.style.setProperty('--ap-onAccent', themeOnAccent(id));
  const full = externalThemeStyles[id as keyof typeof externalThemeStyles];
  root.style.setProperty('--ap-secondary-foreground', full?.secondaryForeground ?? palettes[id].text);
  root.style.setProperty('--ap-onDanger', full?.destructiveForeground ?? themeOnAccent(id));
  root.dataset.themeMode = isDarkTheme(id) ? 'dark' : 'light';
  const palette = palettes[id];
  root.style.setProperty('--ap-feedback-button-bg', isDarkTheme(id) ? palette.canvas : palette.surface);
  for (const [name, role] of Object.entries(aliases)) {
    root.style.setProperty(`--${name}`, `var(--ap-${role})`);
  }
  for (const [role, value] of Object.entries(palette)) {
    if (role !== 'name' && role !== 'description') root.style.setProperty(`--ap-${role}`, value);
  }
  // Compatibility tokens preserve the existing theme exactly. New themes resolve
  // all historical CSS shades through the same semantic palette as Ant Design.
  for (const [token, entry] of Object.entries(legacyColors)) {
    const { value, role, alpha } = entry;
    const color = palette[role as ColorRole];
    root.style.setProperty(token, alpha === 1 ? color
      : `color-mix(in srgb, ${color} ${alpha * 100}%, transparent)`);
  }
  listeners.forEach((listener) => listener());
}

export function initializeTheme() {
  let stored: string | null = null;
  try { stored = localStorage.getItem(THEME_STORAGE_KEY); } catch { /* Private mode still supports switching. */ }
  applyTheme(resolveTheme(stored));
}

export function setTheme(id: ThemeId) {
  const valid = resolveTheme(id);
  try { localStorage.setItem(THEME_STORAGE_KEY, valid); } catch { /* Keep the in-memory choice. */ }
  applyTheme(valid);
}

window.addEventListener('storage', (event) => {
  try {
    if (event.storageArea === localStorage && (event.key === THEME_STORAGE_KEY || event.key === null)) applyTheme(resolveTheme(event.newValue));
  } catch { /* Storage may be unavailable; preserve the in-memory theme. */ }
});

export function useTheme() {
  return { themeId: useSyncExternalStore(subscribe, snapshot, snapshot), setTheme };
}

export function ThemeProvider({ children, locale }: PropsWithChildren<Pick<ConfigProviderProps, 'locale'>>) {
  useEffect(installDeferredScrollbarVisibility, []);
  const { themeId } = useTheme();
  const reducedMotion = useDesktopReducedMotion();
  // Ant Design lazily inserts its MotionProvider when parent and child motion
  // differ. Establish that boundary on the first render so preference changes
  // update the token without remounting open dialogs or losing their drafts.
  const initialReducedMotion = useRef(reducedMotion);
  const p = palettes[themeId];
  const printing = useSyncExternalStore(subscribePrint, printSnapshot, () => false);
  const full = printing ? undefined : externalThemeStyles[themeId as keyof typeof externalThemeStyles];
  const radius = full?.radius ?? 10;
  const controlRadius = full ? Math.max(0, radius - 4) : 7;
  return <ConfigProvider theme={{ token: { motion: initialReducedMotion.current } }}>
    <ConfigProvider locale={locale} componentSize="middle" theme={{
    algorithm: isDarkTheme(themeId) ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    token: {
      motion: !reducedMotion,
      colorPrimary: p.primary, colorInfo: p.info, colorText: p.text, colorTextHeading: p.ink,
      colorTextSecondary: p.muted, colorTextTertiary: p.subtle, colorTextQuaternary: p.subtle,
      colorTextDescription: p.muted, colorTextPlaceholder: p.subtle, colorTextDisabled: p.subtle,
      colorBgLayout: p.canvas, colorBgContainer: p.surface, colorBgElevated: full?.popover ?? p.surface,
      colorBorder: p.border, colorSplit: p.border, colorFillAlter: p.soft, colorBgContainerDisabled: p.soft,
      colorLink: p.primary, colorLinkHover: p.primaryStrong,
      colorSuccess: p.success, colorSuccessBg: p.successSoft, colorSuccessBorder: p.successSoft,
      colorWarning: p.warning, colorWarningBg: p.warningSoft, colorWarningBorder: p.warningSoft,
      colorError: p.danger, colorErrorBg: p.dangerSoft, colorErrorBorder: p.dangerSoft,
      colorInfoBg: p.infoSoft, colorInfoBorder: p.infoSoft,
      borderRadius: radius, borderRadiusLG: radius, borderRadiusSM: controlRadius,
      boxShadow: full?.shadowLG, boxShadowSecondary: full?.shadowXL,
      controlHeight: 36, controlHeightSM: 28, controlHeightLG: 44,
      fontFamily: full?.fontFamily ?? '-apple-system, BlinkMacSystemFont, "PingFang SC", "Microsoft YaHei", sans-serif', fontSize: 14,
    },
    components: {
      Button: { borderRadius: controlRadius, defaultShadow: full?.shadowXS ?? 'none', primaryShadow: full?.shadowXS ?? 'none', dangerShadow: full?.shadowXS ?? 'none', fontWeight: 500,
        colorPrimary: p.accent, colorPrimaryHover: p.accentLight, colorPrimaryActive: p.accentDark, primaryColor: themeOnAccent(themeId),
        defaultBg: p.soft, defaultColor: full?.secondaryForeground ?? p.text, defaultBorderColor: 'transparent',
        defaultHoverBg: `color-mix(in srgb, ${p.text} 10%, ${p.soft})`, defaultHoverColor: p.text, defaultHoverBorderColor: 'transparent',
        defaultActiveBg: `color-mix(in srgb, ${p.text} 18%, ${p.soft})`, defaultActiveColor: p.text, defaultActiveBorderColor: 'transparent' },
      Input: { borderRadius: controlRadius, hoverBorderColor: full?.ring ?? p.primary, activeBorderColor: full?.ring ?? p.primary, activeShadow: full ? `0 0 0 2px ${full.ring}` : 'none' },
      InputNumber: { borderRadius: controlRadius, hoverBorderColor: full?.ring ?? p.primary, activeBorderColor: full?.ring ?? p.primary, activeShadow: full ? `0 0 0 2px ${full.ring}` : 'none' },
      Select: { borderRadius: controlRadius, optionSelectedBg: p.primarySoft, optionSelectedColor: full?.accentForeground ?? p.text, optionActiveBg: p.soft, activeBorderColor: p.primary, hoverBorderColor: p.primary, activeOutlineColor: full?.ring ?? 'transparent' },
      DatePicker: { borderRadius: controlRadius, hoverBorderColor: full?.ring ?? p.primary, activeBorderColor: full?.ring ?? p.primary, activeShadow: full ? `0 0 0 2px ${full.ring}` : 'none' },
      Checkbox: { colorPrimary: p.accent, colorPrimaryHover: p.accentDark, colorWhite: themeOnAccent(themeId) },
      Radio: { colorPrimary: p.accent, colorPrimaryHover: p.accentDark },
      Switch: { colorPrimary: p.accent, colorPrimaryHover: p.accentDark, colorTextLightSolid: themeOnAccent(themeId) },
      Tag: { borderRadiusSM: controlRadius, fontSizeSM: 12, defaultBg: p.soft, defaultColor: full?.secondaryForeground ?? p.text,
        colorSuccessText: p.success, colorSuccessBg: p.successSoft, colorSuccessBorder: p.successSoft,
        colorWarningText: p.warning, colorWarningBg: p.warningSoft, colorWarningBorder: p.warningSoft,
        colorErrorText: p.danger, colorErrorBg: p.dangerSoft, colorErrorBorder: p.dangerSoft },
      Card: { borderRadiusLG: radius, headerHeight: 54 },
      Table: { headerBg: p.soft, headerColor: full?.secondaryForeground ?? p.ink, borderColor: p.border, rowHoverBg: p.primarySoft, headerBorderRadius: radius },
      Tabs: { itemSelectedColor: p.primary, inkBarColor: p.accent, horizontalItemGutter: 28 },
      Drawer: { colorBgElevated: full?.popover ?? p.surface }, Modal: { borderRadiusLG: radius },
      Menu: { itemColor: p.text, itemHoverBg: p.primarySoft, itemHoverColor: p.ink,
        itemSelectedBg: p.primarySoft, itemSelectedColor: p.primaryStrong,
        itemDisabledColor: p.subtle, groupTitleColor: p.muted, itemBorderRadius: controlRadius },
      Segmented: { itemSelectedBg: p.surface, trackBg: p.soft },
    },
  }}>{children}</ConfigProvider></ConfigProvider>;
}
